// packages/web/src/pages/TermsPage.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 6, 0),
  textAlign: 'center',
  borderBottom: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0, 4, 0),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(2.5),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  '&:first-of-type': {
    marginTop: 0,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const SubsectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1.5),
  color: '#1a1a1a',
}));

const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.8,
  fontWeight: 400,
  marginBottom: theme.spacing(2),
}));

const BulletList = styled('ul')(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  marginBottom: theme.spacing(2),
  '& li': {
    fontSize: '1rem',
    color: '#666',
    lineHeight: 1.8,
    marginBottom: theme.spacing(1),
  },
}));

const TermsOfServicePage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      <HeroSection>
        <Container maxWidth="md">
          <PageTitle>Terms of Service</PageTitle>
          <PageSubtitle>
            Last updated: January 1, 2025
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="md">
          <BodyText>
            Welcome to Sched Tennis. By accessing or using our service, you agree to be bound by these 
            Terms of Service. Please read them carefully.
          </BodyText>

          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <BodyText>
            By creating an account and using Sched Tennis, you acknowledge that you have read, understood, 
            and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree 
            to these terms, please do not use our service.
          </BodyText>

          <SectionTitle>2. Description of Service</SectionTitle>
          <BodyText>
            Sched Tennis provides a platform for tennis players to:
          </BodyText>
          <BulletList>
            <li>Publish their tennis availability schedules</li>
            <li>Browse other players' schedules</li>
            <li>Send and receive match challenges</li>
            <li>Coordinate tennis matches with other users</li>
            <li>Find and view information about tennis courts</li>
          </BulletList>

          <SectionTitle>3. User Accounts</SectionTitle>
          <SubsectionTitle>3.1 Account Creation</SubsectionTitle>
          <BodyText>
            To use certain features of Sched Tennis, you must create an account using Google Single Sign-On (SSO). 
            You are responsible for maintaining the confidentiality of your account credentials.
          </BodyText>
          <SubsectionTitle>3.2 Account Responsibilities</SubsectionTitle>
          <BodyText>
            You are responsible for all activities that occur under your account. You agree to:
          </BodyText>
          <BulletList>
            <li>Provide accurate and complete information</li>
            <li>Maintain and update your information to keep it accurate</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Not share your account credentials with others</li>
          </BulletList>

          <SectionTitle>4. User Conduct</SectionTitle>
          <BodyText>
            You agree to use Sched Tennis in a respectful and lawful manner. You will not:
          </BodyText>
          <BulletList>
            <li>Harass, abuse, or harm other users</li>
            <li>Provide false or misleading information about your availability or skill level</li>
            <li>Use the service for any illegal purposes</li>
            <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
            <li>Send spam or unsolicited messages to other users</li>
            <li>Post content that is offensive, inappropriate, or violates others' rights</li>
            <li>Interfere with or disrupt the service or servers</li>
          </BulletList>

          <SectionTitle>5. Match Coordination</SectionTitle>
          <SubsectionTitle>5.1 Scheduling</SubsectionTitle>
          <BodyText>
            Sched Tennis is a coordination platform only. We do not guarantee that matches will occur, 
            and we are not responsible for disputes between users regarding match scheduling or cancellations.
          </BodyText>
          <SubsectionTitle>5.2 Court Availability</SubsectionTitle>
          <BodyText>
            Users are responsible for ensuring court availability before scheduling matches. Sched Tennis 
            does not book courts or guarantee court access.
          </BodyText>
          <SubsectionTitle>5.3 Safety</SubsectionTitle>
          <BodyText>
            Always prioritize your safety when meeting other players. Consider meeting in public places 
            and informing someone of your plans.
          </BodyText>

          <SectionTitle>6. Content and Intellectual Property</SectionTitle>
          <SubsectionTitle>6.1 Your Content</SubsectionTitle>
          <BodyText>
            You retain ownership of any content you post on Sched Tennis, including your schedule and 
            profile information. By posting content, you grant us a license to use, display, and 
            distribute that content as necessary to provide the service.
          </BodyText>
          <SubsectionTitle>6.2 Our Content</SubsectionTitle>
          <BodyText>
            All content provided by Sched Tennis, including the platform design, features, and functionality, 
            is owned by us and protected by intellectual property laws. You may not copy, modify, or 
            distribute our content without permission.
          </BodyText>

          <SectionTitle>7. Privacy</SectionTitle>
          <BodyText>
            Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
            use, and protect your information.
          </BodyText>

          <SectionTitle>8. Termination</SectionTitle>
          <BodyText>
            We reserve the right to suspend or terminate your account at any time for violations of these 
            Terms of Service or for any other reason. You may also delete your account at any time through 
            your account settings.
          </BodyText>

          <SectionTitle>9. Disclaimers</SectionTitle>
          <BodyText>
            Sched Tennis is provided "as is" without warranties of any kind. We do not guarantee that:
          </BodyText>
          <BulletList>
            <li>The service will be uninterrupted or error-free</li>
            <li>Matches arranged through the platform will occur as scheduled</li>
            <li>Other users' information is accurate or reliable</li>
            <li>The service will meet your specific requirements</li>
          </BulletList>

          <SectionTitle>10. Limitation of Liability</SectionTitle>
          <BodyText>
            To the maximum extent permitted by law, Sched Tennis and its operators shall not be liable 
            for any indirect, incidental, special, consequential, or punitive damages resulting from your 
            use of the service. This includes injuries, property damage, or disputes arising from matches 
            coordinated through the platform.
          </BodyText>

          <SectionTitle>11. Changes to Terms</SectionTitle>
          <BodyText>
            We may update these Terms of Service from time to time. We will notify users of significant 
            changes by posting a notice on the platform or sending an email. Your continued use of Sched 
            Tennis after changes are posted constitutes acceptance of the updated terms.
          </BodyText>

          <SectionTitle>12. Governing Law</SectionTitle>
          <BodyText>
            These Terms of Service shall be governed by and construed in accordance with applicable laws. 
            Any disputes arising from these terms or your use of the service shall be resolved in accordance 
            with such laws.
          </BodyText>

          <SectionTitle>13. Contact Us</SectionTitle>
          <BodyText>
            If you have any questions about these Terms of Service, please contact us through our Contact page.
          </BodyText>
        </Container>
      </ContentSection>

      <Footer />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default TermsOfServicePage;
